import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Stack, Typography, Box } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import s from "./AccountStatusDialog.module.scss";
import AgentStatusOptions from "utils/AgentStatusOptions/AgentStatusOptions";
import Selection from "components/Input/Selection/Selection";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import { isNullish } from "utils/utils";
import { dateFormat } from "utils/utils";
const DIALOG_LOCALE_KEY = "page.dialogs.";
const remarkOthersKey = "OTHERS";

const AccountStatusDialog = ({
  data,
  remarksList,
  updatePayload,
  includePending = false,
  onboardRemarks,
}) => {
  const { t } = useTranslation();
  const [focus, setFocus] = useState({});
  const safeRemarksList =
    remarksList && typeof remarksList === "object" ? remarksList : {};

  if (safeRemarksList["TERMINATED"]) {
    safeRemarksList["TERMINATED"][remarkOthersKey] = t(
      DIALOG_LOCALE_KEY + "changeStatus.remarkOthers.value",
    );
  }
  if (safeRemarksList["SUSPENDED"]) {
    safeRemarksList["SUSPENDED"][remarkOthersKey] = t(
      DIALOG_LOCALE_KEY + "changeStatus.remarkOthers.value",
    );
  }
  if (safeRemarksList["PENDING"]) {
    safeRemarksList["PENDING"][remarkOthersKey] = t(
      DIALOG_LOCALE_KEY + "changeStatus.remarkOthers.value",
    );
  }

  const INPUT_FIELD_INIT_STATE = {
    status: data.status,
    reason: data.reason ?? data.lockAccountType ?? "",
    remark: data.remark ? data.remark : "",
  };

  const [fieldInput, setFieldInput] = useState(INPUT_FIELD_INIT_STATE);

  // Reset fieldInput when data changes (e.g., when dialog opens/closes)
  useEffect(() => {
    setFieldInput({
      status: data.status,
      reason: data.reason ?? data.lockAccountType ?? "",
      remark: data.remark || "",
    });
    setReasonList(safeRemarksList[data.status] ?? {});
  }, [data.status, data.reason, data.lockAccountType, data.remark, safeRemarksList]);

  const [reasonList, setReasonList] = useState(safeRemarksList[data.status] ?? {});

  const statusOnChange = (e, callback) => {
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    updateReasonList(value);
  };

  const textInputOnChange = (e, field, callback) => {
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    setFieldInput({ ...fieldInput, [field]: value });
    updatePayload({ ...fieldInput, [field]: value });
  };

  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };

  const textInputOnBlur = (field, callback) => {
    if (fieldInput[field] && callback) {
      const isValidValue = callback(fieldInput[field]);
      if (!isValidValue) {
        setError({ ...error, [field]: error });
      }
    }
    setFocus({ [field]: false });
  };
  const isInputRemarksDisabled = () => {
    if (fieldInput.status === "PENDING") {
      return false;
    }
    return !(fieldInput.reason == remarkOthersKey);
  };
  const updateReasonList = (newstatus) => {
    setReasonList(safeRemarksList[newstatus] ?? {});
    setFieldInput({ ...fieldInput, status: newstatus, reason: "", remark: "" });
    updatePayload({ ...fieldInput, status: newstatus, reason: "", remark: "" });
  };

  const getReasonListEmpty = () => {
    if (isNullish(reasonList)) return true;
    return Object.keys(reasonList).length == 0;
  };

  const getReasonList = () => {
    if (!isNullish(reasonList)) {
      const keys = Object.keys(reasonList);
      return keys.map((reason) => {
        return {
          key: reason,
          title: reasonList[reason],
          value: reason,
        };
      });
    }
    return [];
  };

  return (
    <div className={s.contentContainer}>
      <Selection
        label={t(DIALOG_LOCALE_KEY + "changeStatus.selectStatus.label")}
        selectionValue={fieldInput.status}
        onchangeFn={(e) => statusOnChange(e)}
        onfocusFn={() => textInputOnFocus("status")}
        onblurFn={() => textInputOnBlur("status")}
        options={AgentStatusOptions("all", includePending)}
        sxParams={{ selectionFocus: focus.status }}
      />
      <div className={getReasonListEmpty() ? s.inactive : ""}>
        <Selection
          label={t(DIALOG_LOCALE_KEY + "changeStatus.selectReason.label")}
          selectionValue={fieldInput.reason}
          onchangeFn={(e) => textInputOnChange(e, "reason")}
          onfocusFn={() => textInputOnFocus("reason")}
          onblurFn={() => textInputOnBlur("reason")}
          options={getReasonList()}
          sxParams={{ selectionFocus: focus.reason }}
          disabled={getReasonListEmpty()}
        />
      </div>
      {/* Onboard remarks history (e.g., KYC) */}
      {fieldInput.status === "PENDING" && (
        <Stack gap={0.5} >
          <Typography variant="body2" className={s.kycHistoryTitle}>
            {t(DIALOG_LOCALE_KEY + "changeStatus.kycHistory.title")}
          </Typography>
          {Array.isArray(onboardRemarks?.KYC) && onboardRemarks.KYC.length > 0 ? (
            <Stack gap={0.5} className={s.kycHistoryList}>
              {[...onboardRemarks.KYC]
                .sort(
                  (a, b) => b.created - a.created
                )
                .map((item, index) => {                  
                  return (
                    <Box
                      key={item.id || `${item.created}-${item.remarks}-${index}`}
                      className={s.kycHistoryContainer}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" className={s.kycHistoryMetaRow}>
                        <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                          <AccessTimeIcon sx={{ fontSize: "12px" }} />
                          <Typography variant="caption" className={s.kycHistoryText}>
                            {dateFormat(item.created)}
                          </Typography>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                          <PersonIcon sx={{ fontSize: "12px" }} />
                          <Typography variant="caption" className={s.kycHistoryText}>
                            {item.createdBy || "-"}
                          </Typography>
                        </div>
                      </Stack>
                      <Typography 
                        variant="body2" 
                        className={s.kycHistoryText}
                      >
                        {item.remarks || "-"}
                      </Typography>
                    </Box>
                  );
                })}
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic", padding: "8px" }}>
              {t(DIALOG_LOCALE_KEY + "changeStatus.kycHistory.noHistory")}
            </Typography>
          )}
        </Stack>
      )}
      <div className={isInputRemarksDisabled() && fieldInput.status !== "PENDING" ? s.inactive : ""}>
        <OutlinedText
          label={
            fieldInput.status === "PENDING"
              ? t(DIALOG_LOCALE_KEY + "changeStatus.inputRemark.kycNoteLabel")
              : t(DIALOG_LOCALE_KEY + "changeStatus.inputRemark.label")
          }
          mandatory={fieldInput.status === "PENDING"}
          value={fieldInput.remark}
          onchangeFn={(e) => textInputOnChange(e, "remark")}
          onfocusFn={() => textInputOnFocus("remark")}
          onblurFn={() => textInputOnBlur("remark")}
          sxParams={{ textFocus: focus.remark }}
          disabled={isInputRemarksDisabled()}
          autoFocus
        />
      </div>
    </div>
  );
};

export default AccountStatusDialog;
